import { useEffect, useState } from 'react';

/**
 * Sparrow customer context — passed in from the parent Shopify storefront
 * via window.postMessage when the iframe loads.
 *
 * Flow
 * ────
 * 1. The Liquid section that mounts the iframe renders {{ customer.id }},
 *    {{ customer.email }}, plus a server-signed HMAC{customer.id|timestamp}.
 * 2. On iframe ready, parent posts a message:
 *      { type: 'SPARROW_CUSTOMER', customerId, email, signature, timestamp }
 * 3. This hook listens for that message and stores the context.
 *
 * If no logged-in customer (or the message never arrives), the hook returns
 * null and the app stays in local-only mode (unchanged behavior).
 *
 * Why HMAC?
 * ─────────
 * The iframe is at cdn.shopify.com — a different origin from the storefront —
 * so we can't trust a customerId on its own. The HMAC is signed server-side
 * by Liquid using a shared secret known only to the theme and the sync Worker.
 * When the iframe posts {customerId, sig, ts} to the Worker, the Worker
 * verifies sig matches HMAC(customerId|ts) before reading/writing metafields.
 *
 * The timestamp limits replay attacks — Worker rejects ts older than ~10min.
 */

export interface CustomerContext {
  customerId: string;
  email: string | null;
  /** HMAC signature of "{customerId}|{timestamp}" using the shared sync secret. */
  signature: string;
  /** Unix timestamp (seconds) when the signature was minted. */
  timestamp: number;
}

interface SparrowCustomerMessage {
  type: 'SPARROW_CUSTOMER';
  customerId: string;
  email?: string | null;
  signature: string;
  timestamp: number;
}

function isSparrowCustomerMessage(value: unknown): value is SparrowCustomerMessage {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return v.type === 'SPARROW_CUSTOMER'
    && typeof v.customerId === 'string' && v.customerId.length > 0
    && typeof v.signature === 'string' && v.signature.length > 0
    && typeof v.timestamp === 'number' && Number.isFinite(v.timestamp);
}

export function useCustomer(): CustomerContext | null {
  const [customer, setCustomer] = useState<CustomerContext | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Announce that the iframe is ready so the parent can send the customer
    // context now (or has already). The parent listens for this on iframe
    // load and replays the message if necessary.
    try {
      window.parent?.postMessage({ type: 'SPARROW_READY' }, '*');
    } catch {
      // Cross-origin parent.postMessage with '*' targetOrigin is always safe —
      // the catch is just for non-iframe (top-level) contexts.
    }

    const onMessage = (event: MessageEvent) => {
      // Only accept messages we know the shape of. We don't enforce origin
      // because Shopify storefront and CDN iframe live on different origins
      // and the HMAC is the actual auth. Anyone forging the message still
      // can't forge the signature.
      if (!isSparrowCustomerMessage(event.data)) return;
      setCustomer({
        customerId: event.data.customerId,
        email: event.data.email ?? null,
        signature: event.data.signature,
        timestamp: event.data.timestamp,
      });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return customer;
}
